import { Button, Modal, toast } from "@devhop/ui";
import { useState } from "react";
import type { KPIListItem } from "@/modules/kpi/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { useCreateChecklistKpiMapping } from "../api/queries";

type KpiOption = { id: string; name: string };

type ChecklistKpiMappingAddModalProps = {
  checklistId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  excludedKpiIds: string[];
  onSuccess?: () => void;
};

export function ChecklistKpiMappingAddModal({
  checklistId,
  open,
  onOpenChange,
  excludedKpiIds,
  onSuccess,
}: ChecklistKpiMappingAddModalProps) {
  const [selectedKpiId, setSelectedKpiId] = useState("");
  const createMapping = useCreateChecklistKpiMapping(checklistId);

  const handleSubmit = async () => {
    if (!selectedKpiId.trim()) {
      toast.error("ກະລຸນາເລືອກ KPI");
      return;
    }
    try {
      await createMapping.mutateAsync({ kpiId: selectedKpiId });
      toast.success("ເພີ່ມການຜູກ KPI ສຳເລັດ");
      setSelectedKpiId("");
      onOpenChange(false);
      onSuccess?.();
    } catch {
      toast.error("ເພີ່ມການຜູກ KPI ລົ້ມເຫຼວ");
    }
  };

  const kpiQueryFn = async ({
    search,
    pageParam,
  }: {
    search: string;
    pageParam: number;
  }) => {
    const limit = 20;
    // InfiniteCombobox ใช้ initialPageParam: 1 (หน้าแรก = 1) ดังนั้น offset = (pageParam - 1) * limit
    const offset = (pageParam - 1) * limit;
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    if (search.trim()) {
      params.set(
        "filters",
        JSON.stringify([{ field: "name", op: "contains", value: search }]),
      );
    }
    const res = await fetcher.get<{
      data: KPIListItem[];
      meta: { total: number; limit: number; offset: number };
    }>(`${config.apiUrl}/kpi?${params}`);
    const items: KpiOption[] = res.data
      .filter((d) => !excludedKpiIds.includes(d.id))
      .map((d) => ({ id: d.id, name: d.name }));
    const hasMore = res.meta.offset + res.meta.limit < res.meta.total;
    const nextPage = hasMore ? pageParam + 1 : null;
    return { items, nextPage };
  };

  const kpiPreloadFn = async (id: string): Promise<KpiOption | null> => {
    const kpi = await fetcher.get<{ id: string; name: string }>(
      `${config.apiUrl}/kpi/${id}`,
    );
    return kpi ? { id: kpi.id, name: kpi.name } : null;
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="ເພີ່ມ KPI ທີ່ດຶງຄ່າຈາກ checklist ນີ້"
      size="md"
    >
      <div className="space-y-4">
        <p className="text-muted-foreground text-sm">
          ເມື່ອມີການເຮັດ checklist ນີ້ແລ້ວ (status ເປັນ ສຳເລັດ) ຄ່າ KPI
          ທີ່ເລືອກຈະຖືກອັບເດດອັດຕະໂນມັດ
        </p>
        <InfiniteCombobox<KpiOption>
          queryKey={["kpi", "lookup", checklistId]}
          queryFn={kpiQueryFn}
          preloadQueryFn={kpiPreloadFn}
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
          value={selectedKpiId}
          onValueChange={setSelectedKpiId}
          placeholder="ເລືອກ KPI..."
          clearable
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            ຍົກເລີກ
          </Button>
          <Button onClick={handleSubmit} disabled={createMapping.isPending}>
            {createMapping.isPending ? "ກຳລັງເພີ່ມ..." : "ເພີ່ມ"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
